// BotSwitcher.tsx — the rail's bot switcher (handoff README §1 "Bot switcher — the one-bot
// majority"; Shell Anatomy.dc.html §03 "One bot vs five").
//
// DEPARTS FROM THE HANDOFF (Task 1, dashboard-shell-feedback plan, 2026-08-09): §1 argues for a
// non-interactive identity card at `bots.length === 1` — no chevron, `cursor: default` — because
// Free/Starter allow exactly one bot, so nothing to switch to. Dev testing (Johnny) found the
// cost the handoff didn't foresee: that card was the ONLY route to `/bots`, so a Free/Starter
// workspace had no way to discover multi-bot plans exist short of typing the URL, and no upsell
// surface at the moment someone wants a second bot. The switcher is now ALWAYS a popover
// trigger, at any bot count. The card's visual treatment — status dot, host domain — is
// unchanged; only its interactivity changes. The "YOUR BOTS · N OF CAP USED" header line below
// and the `New bot — N of CAP used` footer row (now a real link to `/billing`, not inert text)
// are what carry the discoverability the identity card used to block.
import { useState } from 'react';
import { ChevronsUpDownIcon, ListIcon, PlusIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/components/popover';
import { StatusChip } from '@repo/ui/components/status-chip';
import type { BotDto, BotListItemDto, SourceDto } from '@repo/shared-types';
import { useBotsWithHealth } from '../../hooks/useBots';
import { useBillingSummary } from '../../hooks/useBillingSummary';
import { apiFetch } from '../../lib/api';
import { botCapGate, botRowView, deriveDomain } from '../../lib/bots-list';
import { useT } from '../../locales/context';

function BotMark({ name, size }: { name: string; size: number }) {
  return (
    <span
      className="grid shrink-0 place-items-center rounded-lg bg-[color-mix(in_oklch,var(--primary)_var(--st-wash),transparent)] font-display font-bold text-primary"
      style={{ width: size, height: size, fontSize: size === 28 ? 14 : 13, borderRadius: size === 28 ? 8 : 7 }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

/** The shared visual: 28px mark, name, a status dot + the derived domain (dot alone when
 * there's no domain to show — README §1's "identity card" language, "Context the brief
 * cannot know" clarifies: fall back to showing nothing rather than inventing a placeholder),
 * and a chevron — the switcher is always a popover trigger now (see file header), so the
 * chevron is no longer conditional on bot count. */
function SwitcherCard({ bot, domain }: { bot: BotDto; domain: string | null }) {
  const dotVar = bot.status === 'active' ? '--st-ready' : '--st-paused';
  return (
    <div className="flex w-full items-center gap-[9px] rounded-[10px] border border-border bg-card px-2.5 py-[9px] shadow-el-1">
      <BotMark name={bot.name} size={28} />
      <span className="flex min-w-0 flex-1 flex-col gap-px text-left">
        <span className="truncate text-[13.5px] font-semibold text-foreground">{bot.name}</span>
        <span className="flex items-center gap-[5px] overflow-hidden font-mono text-[11px] text-muted-foreground">
          <span
            aria-hidden="true"
            className="size-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: `var(${dotVar})` }}
          />
          {domain && <span className="truncate">{domain}</span>}
        </span>
      </span>
      <ChevronsUpDownIcon size={14} strokeWidth={2.2} className="shrink-0 text-muted-foreground" />
    </div>
  );
}

/** One row inside the open popover: mark, name, a mono health line, and its own status chip.
 * `SourceStatus` values line up exactly with `StatusChipState`, so `worstStatus` maps
 * directly — no separate lookup table. Chip state + health-line text come from `botRowView`
 * (`lib/bots-list.ts`, final review, source-detail-diagnostics — moved out of this file so it
 * has a real test and this file stays component-only exports for Fast Refresh). See that
 * function's own doc comment for the `ready`+`deleting`-mix fix. */
function BotRow({
  bot,
  health,
  current,
  onSelect,
}: {
  bot: BotDto;
  health: BotListItemDto['health'] | undefined;
  current: boolean;
  onSelect: () => void;
}) {
  const t = useT();
  const { state, healthLine } = botRowView(bot, health, t);

  const rowContent = (
    <>
      <BotMark name={bot.name} size={26} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[13px] font-semibold text-foreground">{bot.name}</span>
        <span className="font-mono text-[10.5px] text-muted-foreground">{healthLine}</span>
      </span>
      <StatusChip state={state} label={t.status[state]} className="shrink-0 gap-[5px] px-2 py-0.5 text-[11px]" />
    </>
  );

  // Switcher nit (fix-wave): the current bot's row would otherwise be a button whose only
  // action is navigating to the bot you're already on — a no-op affordance, worst-case visible
  // at `bots.length === 1` where it's the ONLY row in the menu. Rendered as a plain `div` (no
  // `onClick`, no hover/focus affordance) instead, keeping the same teal wash that already
  // marks it current.
  if (current) {
    return (
      <div
        aria-current="true"
        className="flex w-full items-center gap-[9px] rounded-lg bg-[color-mix(in_oklch,var(--primary)_var(--st-wash),transparent)] px-2 py-[7px] text-left"
      >
        {rowContent}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-[9px] rounded-lg px-2 py-[7px] text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
    >
      {rowContent}
    </button>
  );
}

/** The popover trigger + its contents — every bot count now (see file header for why this used
 * to be `MultiBotSwitcher`, gated to `bots.length > 1`). `New bot` and `All bots` are two
 * different destinations: `All bots` always goes to the list; `New bot` goes to `/bots?new=1`,
 * which opens `Bots.tsx`'s existing create dialog on arrival, OR — at the plan's bot cap — to
 * `/billing`, reading as `New bot — N of CAP used` rather than inert disabled text. That gate is
 * `lib/bots-list.ts`'s `botCapGate`, the same function `Bots.tsx`'s own upgrade box uses, so the
 * two screens can never disagree about when the cap is reached (`cap === null`, an internal
 * workspace, never gates — `botCapGate`'s own contract). */
function SwitcherPopover({
  bots,
  currentBot,
  domain,
}: {
  bots: BotDto[];
  currentBot: BotDto;
  domain: string | null;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const t = useT();
  // Fix-wave item 4: gated on `open`, not unconditional. `SwitcherPopover` now mounts on every
  // authenticated route (Task 1 made the switcher always a dropdown, including at one bot), so
  // an unconditional `useBotsWithHealth(true)` here would pull every message row across every
  // bot for 14 days plus the unanswered pairs — refreshed every 60s — on every route for every
  // workspace, for data (`BotRow`'s health line + chip) that only ever renders inside a closed-
  // by-default popover. `useBillingSummary()` below stays unconditional on purpose: it's cheap,
  // and `cap`/`gate.atCap` (used to pick the `New bot` row's destination) are computed from it
  // regardless of whether the popover is open.
  const { data: health } = useBotsWithHealth(open);
  const { data: billing } = useBillingSummary();

  const cap = billing?.plan.caps.bots ?? null;
  const gate = botCapGate(bots.length, cap);

  function selectBot(id: string) {
    setOpen(false);
    navigate(`/bots/${id}`);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // Switcher nit (fix-wave): without this, the accessible name is whatever text nodes
          // `SwitcherCard` renders concatenated together (name + domain), with nothing telling
          // a screen-reader user this button opens a menu. Restores the intent of the pre-
          // redesign `aria-label={`${currentBot.name}, current bot`}` in the new "always a
          // popover" shape.
          aria-label={t.shell.switchBotAriaLabel({ name: currentBot.name })}
          className="w-full rounded-[10px] text-left transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35 [&>div]:hover:border-[color-mix(in_oklch,var(--primary)_45%,var(--border))]"
        >
          <SwitcherCard bot={currentBot} domain={domain} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[296px] rounded-xl p-1.5 shadow-el-2">
        <div className="px-2 pt-1.5 pb-1 font-mono text-[10px] tracking-[0.09em] text-muted-foreground">
          {t.shell.botSwitcherHeader({ n: bots.length, cap })}
        </div>
        <div className="flex flex-col gap-0.5">
          {bots.map((bot) => (
            <BotRow
              key={bot.id}
              bot={bot}
              health={health?.find((h) => h.id === bot.id)?.health}
              current={bot.id === currentBot.id}
              onSelect={() => selectBot(bot.id)}
            />
          ))}
        </div>
        <div className="my-1.5 h-px bg-border" />
        <Link
          to="/bots"
          onClick={() => setOpen(false)}
          className="flex w-full items-center gap-2 rounded-lg px-2 py-[7px] text-[12.5px] font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
        >
          <ListIcon size={14} strokeWidth={2} />
          {t.shell.allBots}
        </Link>
        {gate.atCap ? (
          // Fix-wave item 8: `gate.atCap` alone doesn't say whether `/billing` has anything to
          // offer — a Growth workspace at 5/5 has no plan above (`cheapestPlanAbove` returns
          // `null`, `gate.showUpgradeGate` is `false`), the same case `Bots.tsx`'s own upgrade
          // box already suppresses. Route the link only when there's a real upgrade to name;
          // otherwise render the same "used up" fact as inert text, matching `Bots.tsx`'s
          // disabled `New bot` button rather than sending someone to a page with nothing to buy.
          gate.showUpgradeGate ? (
            <Link
              to="/billing"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-[7px] text-[12.5px] font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
            >
              <PlusIcon size={14} strokeWidth={2} />
              {t.shell.newBotOfCapUsed({ n: bots.length, cap: gate.cap! })}
            </Link>
          ) : (
            <div className="flex w-full cursor-default items-center gap-2 rounded-lg px-2 py-[7px] text-[12.5px] font-medium text-muted-foreground opacity-60">
              <PlusIcon size={14} strokeWidth={2} />
              {t.shell.newBotOfCapUsed({ n: bots.length, cap: gate.cap! })}
            </div>
          )
        ) : (
          <Link
            to="/bots?new=1"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 rounded-lg px-2 py-[7px] text-[12.5px] font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
          >
            <PlusIcon size={14} strokeWidth={2} />
            {t.shell.newBot}
          </Link>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function BotSwitcher({ bots, currentBotId }: { bots: BotDto[]; currentBotId: string | undefined }) {
  const currentBot = bots.find((b) => b.id === currentBotId) ?? bots[0];

  // Same query key BotSources.tsx fetches under, so a visit to Sources and the rail's own
  // mount share one cache entry rather than two independent fetches of the same list.
  const { data: sources } = useQuery({
    queryKey: ['bots', currentBot?.id, 'sources'],
    queryFn: () => apiFetch<SourceDto[]>(`/api/bots/${currentBot!.id}/sources`),
    enabled: !!currentBot,
    staleTime: 60_000,
  });
  const domain = deriveDomain(sources);

  if (!currentBot) return null; // zero-bot workspace — nothing to switch between

  return <SwitcherPopover bots={bots} currentBot={currentBot} domain={domain} />;
}
